import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function HowItWorksPage() {
  const steps = [
    {
      number: '01',
      title: 'Pre-Qualification',
      subtitle: 'Understanding Your Current Position',
      description: 'We begin with a comprehensive assessment of your current financial position, business structure, and funding goals.',
      details: [
        'Review of personal and business credit profiles',
        'Analysis of current business structure and compliance',
        'Assessment of funding needs and timeline',
        'Identification of immediate opportunities',
        'Clear roadmap of what\'s possible and what needs work'
      ],
      icon: 'ri-file-list-3-line'
    },
    {
      number: '02',
      title: 'Fundability Audit',
      subtitle: 'Identifying Gaps and Opportunities',
      description: 'A detailed review of everything lenders will see when evaluating your application.',
      details: [
        'Credit report analysis (personal and business)',
        'Business entity and compliance review',
        'Documentation and record-keeping assessment',
        'Banking relationship evaluation',
        'Identification of red flags and fundability issues',
        'Prioritized action plan for improvements'
      ],
      icon: 'ri-search-line'
    },
    {
      number: '03',
      title: 'Positioning & Packaging',
      subtitle: 'Preparing You to Qualify',
      description: 'Strategic preparation of your profile, documentation, and business structure to maximize approval rates.',
      details: [
        'Credit profile optimization strategies',
        'Business structure and compliance corrections',
        'Documentation preparation and organization',
        'Banking relationship development',
        'Tradeline and credit history building',
        'Application timing and sequencing strategy'
      ],
      icon: 'ri-file-edit-line'
    },
    {
      number: '04',
      title: 'Submission Strategy',
      subtitle: 'Executing with Precision',
      description: 'Targeted application approach using underwriter-level insights to match you with the right funding sources.',
      details: [
        'Lender matching based on your profile',
        'Strategic application timing and sequencing',
        'Professional application preparation',
        'Documentation package assembly',
        'Follow-up and communication management',
        'Negotiation support when needed'
      ],
      icon: 'ri-send-plane-line'
    },
    {
      number: '05',
      title: 'Funding & Scaling Roadmap',
      subtitle: 'Long-Term Success Strategy',
      description: 'Post-approval guidance and ongoing strategy to maintain and expand your funding capacity.',
      details: [
        'Credit utilization and management guidance',
        'Account maintenance best practices',
        'Future funding opportunity identification',
        'Credit limit increase strategies',
        'Long-term fundability maintenance',
        'Scaling roadmap as your business grows'
      ],
      icon: 'ri-rocket-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-20">
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-xs uppercase tracking-wider text-gray-600 mb-4 font-semibold">
              OUR PROCESS
            </p>
            <h1 className="text-5xl md:text-6xl font-bold text-navy mb-6">
              How It Works
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven five-step process designed to maximize your funding success through strategic preparation and expert execution
            </p>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            {steps.map((step, index) => (
              <div key={index} className="relative mb-20 last:mb-0">
                {index < steps.length - 1 && (
                  <div className="absolute left-7 top-20 bottom-0 w-0.5 bg-gray-200 -z-10"></div>
                )}
                
                <div className="flex gap-8">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-navy text-white flex items-center justify-center text-xl font-bold">
                      {step.number}
                    </div>
                  </div>

                  <div className="flex-1 pb-12">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center">
                        <i className={`${step.icon} text-2xl text-navy`}></i>
                      </div>
                      <h2 className="text-3xl font-bold text-navy">
                        {step.title}
                      </h2>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-700 mb-4">
                      {step.subtitle}
                    </h3>

                    <p className="text-base text-gray-600 leading-relaxed mb-6">
                      {step.description}
                    </p>

                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-sm font-semibold text-navy uppercase tracking-wider mb-4">
                        What's Included:
                      </h4>
                      <ul className="space-y-3">
                        {step.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-checkbox-circle-fill text-lg text-navy"></i>
                            </div>
                            <span className="text-sm text-gray-700">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-navy text-center mb-12">
              Why This Process Works
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-eye-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-2">
                  Underwriter Perspective
                </h3>
                <p className="text-sm text-gray-600">
                  We see what lenders see and fix issues before they become problems
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-2">
                  Compliance First
                </h3>
                <p className="text-sm text-gray-600">
                  Everything we do is above board, documented, and built to last
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-line-chart-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-2">
                  Strategic Execution
                </h3>
                <p className="text-sm text-gray-600">
                  Timing, sequencing, and positioning matter — we get it right
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-navy text-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6">
              Ready to Start Your Funding Journey?
            </h2>
            <p className="text-lg text-gray-300 mb-10">
              Let's assess your situation and create your personalized funding strategy
            </p>
            <Link
              to="/apply"
              className="inline-flex items-center bg-white text-navy px-8 py-4 rounded-lg text-base font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Begin Pre-Qualification
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
